import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { FilterSelect } from '~/pages/backoffice/TableToolbar'
import { PAGE_SIZE_OPTIONS } from '~/pages/backoffice/useTableQuery'

type TablePaginationProps = {
  page: number
  pageSize: number
  /** Nombre total de lignes côté serveur, tous écrans de pagination confondus. */
  total?: number | null
  onPageChange: (page: number) => void
  /**
   * Change le nombre de lignes par page. Optionnel : un tableau qui ne le
   * fournit pas n'affiche pas le sélecteur, plutôt que d'en montrer un inerte.
   */
  onPageSizeChange?: (pageSize: number) => void
}

/**
 * Pied de tableau paginé : compteur de résultats, lignes par page,
 * précédent/suivant. Partagé par les tableaux du back-office qui pilotent leur
 * pagination avec `useTableQuery`.
 */
function TablePagination({ page, pageSize, total, onPageChange, onPageSizeChange }: TablePaginationProps) {
  const { t } = useTranslation()
  // `useId` et non un identifiant fixe : le `for` du label doit rester unique
  // le jour où deux tableaux paginés cohabitent sur un même écran.
  const sizeId = useId()

  return (
    <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-3 py-4">
      {/* Région de statut : le nombre de résultats est annoncé après une recherche,
          un filtre ou un changement de page (RGAA 7.5). */}
      <div role="status" className="flex-1 text-sm text-muted-foreground">
        {t('common.resultsCount', { count: total ?? 0 })}
      </div>

      {onPageSizeChange && (
        <div className="flex items-center gap-2">
          <label className="whitespace-nowrap text-sm text-muted-foreground" htmlFor={sizeId}>
            {t('common.rowsPerPage')}
          </label>
          <FilterSelect
            id={sizeId}
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="w-auto"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </FilterSelect>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          {t('common.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!!(total && page * pageSize >= total)}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  )
}

export default TablePagination
